import PropTypes from 'prop-types'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, Route, Routes } from 'react-router-dom'

import { Loader } from './components'
import Layout from './components/layout'
import CompleteSignup from './pages/complete-signup'
import Error404 from './pages/error-404'
import { routes, unauthenticatedRoutes } from './routes/index'

import { checkForUserBranch } from './helpers'
import { trackEvent } from './helpers/analytics'
import { localStorage } from './helpers/local-storage'

import AccessDenied from './components/access-denied'
import ReleaseNotes from './components/release-notes'
import StartupHandler from './components/startup-handler'
import Trial from './components/trial/trial'
import trackEvents from './constants/track-events'
import { DataContext, GeneralContext } from './context'
import { useActiveSpaceQuery } from './hooks/use-active-space-query'
import { useEmployeesQuery } from './hooks/use-employees-query'
import { useMount } from './hooks/use-mount'
import { usePartnerQuery } from './hooks/use-partner-query'
import { useUserQuery } from './hooks/use-user-query'
// import Checkout from './pages/checkout' //
import { usePaymentsQuery } from './hooks/use-payments-query'
import { useSystemQuery } from './hooks/use-system-query'
import { useUserEndpointsQuery } from './hooks/use-user-endpoints-query'

function App({ language, cognitoUser, restartDashboard }) {
  const { t } = useTranslation()

  useMount(() => {
    checkForUserBranch()
    trackEvent(trackEvents.LAUNCH)
  })

  const username = useMemo(() => {
    if (!cognitoUser) return null

    return cognitoUser.username
  }, [cognitoUser])

  const cognitoGroups = useMemo(() => {
    if (!cognitoUser) return []

    const { payload } = cognitoUser.signInUserSession.accessToken
    const groups = [...(payload['cognito:groups'] || [])]

    return groups
  }, [cognitoUser])

  const isAdmin = useMemo(
    () => cognitoGroups.includes('__admin'),
    [cognitoGroups]
  )

  const partner_id = useMemo(() => {
    const active_partner_id_from_groups = cognitoGroups.find((g) =>
      g.includes('__active_partner_id')
    )

    if (!active_partner_id_from_groups) return

    return (
      localStorage.getItem('admin') ||
      active_partner_id_from_groups.split(':').pop()
    )
  }, [cognitoGroups])

  const active_space_id = useMemo(() => {
    const active_space_id_from_groups = cognitoGroups.find((g) =>
      g.includes('__active_space_id')
    )

    if (!active_space_id_from_groups) return

    return active_space_id_from_groups.split(':').pop()
  }, [cognitoGroups])

  const { data: activeSpace, refetch: refetchActiveSpace } =
    useActiveSpaceQuery({
      active_space_id
    })

  const { data: user } = useUserQuery({
    username
  })

  const { data: employees, refetch: refetchEmployees } = useEmployeesQuery({
    username
  })

  const { data: partner, refetch: refetchPartner } = usePartnerQuery({
    partner_id
  })

  const { data: emailEndpoint, refetch: refetchEmailEndpoint } =
    useUserEndpointsQuery({ username })

  const { data: system } = useSystemQuery({ username })

  const { data: payments, accessDenied } = usePaymentsQuery({
    partner_id,
    cognitoUser
  })

  if (accessDenied) {
    return <AccessDenied />
  }

  // show authenticator if !cognitoUser
  const isLoading = cognitoUser
    ? !activeSpace ||
      !system ||
      !partner ||
      !payments?.length ||
      !emailEndpoint ||
      !employees ||
      !user
    : false

  if (isLoading) {
    return <Loader loadingText={t('app.dashboard_loading_text')} />
  }

  if (!cognitoUser) {
    return (
      <GeneralContext language={language}>
        <Routes>
          {unauthenticatedRoutes.map(({ path, Component }) => (
            <Route key={path} path={path} element={<Component />} />
          ))}
        </Routes>
      </GeneralContext>
    )
  }

  return (
    <GeneralContext language={language}>
      <DataContext
        system={system}
        restartDashboard={restartDashboard}
        cognitoUser={{ ...cognitoUser, groups: cognitoGroups, partner_id }}
        activeSpace={activeSpace}
        refetchActiveSpace={refetchActiveSpace}
        isAdmin={isAdmin}
        partner={partner}
        refetchPartner={refetchPartner}
        payments={payments}
        emailEndpoint={emailEndpoint}
        refetchEmailEndpoint={refetchEmailEndpoint}
        employees={employees}
        refetchEmployees={refetchEmployees}
        user={user}
      >
        <StartupHandler />
        <Routes>
          {/* <Route path='/ausprobieren' element={<Tryout />} /> */}
          <Route path='/' element={<Navigate to='/talents' replace />} />
          <Route path='/login' element={<Navigate to='/talents' replace />} />

          {routes.map(({ path, Component, useLayout }) => (
            <Route
              key={path}
              path={path}
              element={
                useLayout ? (
                  <Layout>
                    <Trial />
                    <Component />
                    <ReleaseNotes startup asPopup />
                    {/* <ProductTour /> */}
                  </Layout>
                ) : (
                  <Component />
                )
              }
            />
          ))}

          <Route path='/complete-signup' element={<CompleteSignup />} />

          <Route
            path='/ausprobieren'
            element={<Navigate replace to='/careers' />}
          />
          <Route path='*' element={<Error404 />} />
        </Routes>
      </DataContext>
    </GeneralContext>
  )
}

App.propTypes = {
  language: PropTypes.string.isRequired,
  cognitoUser: PropTypes.object,
  restartDashboard: PropTypes.func.isRequired
}

App.defaultProps = {
  cognitoUser: null
}

export default App
